(() => {
  const installationKey = Symbol.for('kaizen.brewGuideMeasurement');
  if (window[installationKey]) return;
  window[installationKey] = true;
  const measurementId = 'G-VLEGB7ZZTM';
  const chapters = new Set(['rest', 'roast', 'peak', 'multi']);
  const selector = 'coffee-chapter-guide[data-guide-product]';
  const states = new Map();
  const viewed = new Set();
  const lastActions = new Map();
  let privacyEpoch = 0;
  let readinessTimer;
  let readinessAttempts = 0;

  const allowed = () => window['ga-disable-' + measurementId] !== true &&
    window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true &&
    typeof window.gtag === 'function';

  function send(name, guide, chapter, entryPoint) {
    try {
      if (!allowed() || !guide.isConnected || !chapters.has(chapter)) return false;
      const product = guide.dataset.guideProduct;
      const itemId = guide.dataset.guideItemId;
      if (!/^[a-z0-9-]+$/.test(product || '') || !/^\d+$/.test(itemId || '')) return false;
      const role = window.Shopify?.theme?.role;
      const context = window.Shopify?.designMode ? 'editor' : role === 'main' ? 'live' : role ? 'preview' : 'unknown';
      const parameters = {
        guide_product: product,
        item_id: itemId,
        guide_chapter: chapter,
        guide_entry_point: entryPoint,
        storefront_context: context,
        send_to: measurementId,
      };
      if (context !== 'live') {
        parameters.debug_mode = true;
        parameters.traffic_type = 'kaizen_preview';
      }
      window.gtag('event', name, parameters);
      if (context !== 'live') {
        try {
          const root = document.documentElement;
          let status;
          try { status = JSON.parse(root.dataset.kaizenGuideMeasurement || '{}'); } catch { status = {}; }
          const queued = {...status?.queued};
          queued[name] = (queued[name] || 0) + 1;
          root.dataset.kaizenGuideMeasurement = JSON.stringify({queued, event: name, chapter, entryPoint});
        } catch {}
      }
      return true;
    } catch { return false; }
  }

  const selectedChapter = guide => {
    const tab = guide.querySelector('.coffee-chapter-guide__tabs button[aria-selected="true"]');
    return tab?.id.split('-').at(-1);
  };
  const cancelView = state => { clearTimeout(state.timer); state.timer = null; };
  function considerView(guide, state) {
    cancelView(state);
    if (!state.visible || document.hidden || !allowed() || viewed.has(guide.dataset.guideProduct)) return;
    const epoch = privacyEpoch;
    state.timer = setTimeout(() => {
      state.timer = null;
      if (!state.visible || document.hidden || !guide.isConnected || epoch !== privacyEpoch || viewed.has(guide.dataset.guideProduct)) return;
      if (send('brew_guide_view', guide, selectedChapter(guide), 'visibility')) {
        viewed.add(guide.dataset.guideProduct);
        observer?.unobserve(state.controls);
      }
    }, 1000);
  }

  const observer = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
    for (const entry of entries) {
      const guide = entry.target.closest(selector);
      const state = states.get(guide);
      if (!state) continue;
      state.visible = entry.isIntersecting && entry.intersectionRatio >= 0.5;
      considerView(guide, state);
    }
  }, {threshold: [0, 0.5]}) : null;

  function register(root) {
    const guides = root.matches?.(selector) ? [root] : [...(root.querySelectorAll?.(selector) || [])];
    for (const guide of guides) {
      if (states.has(guide)) continue;
      const controls = guide.querySelector('.coffee-chapter-guide__tabs');
      if (!controls) continue;
      const state = {controls, visible: false, timer: null};
      states.set(guide, state);
      observer?.observe(controls);
      if (!readinessTimer) {
        readinessAttempts = 0;
        readinessTimer = setInterval(() => {
          for (const [rootGuide, rootState] of states) {
            if (!rootState.timer && rootState.visible && !viewed.has(rootGuide.dataset.guideProduct)) considerView(rootGuide, rootState);
          }
          if (++readinessAttempts >= 30) { clearInterval(readinessTimer); readinessTimer = null; }
        }, 1000);
      }
    }
  }

  function recordSelection(guide, entryPoint, expectedChapter, before) {
    if (!allowed() || document.hidden) return;
    const epoch = privacyEpoch;
    setTimeout(() => {
      if (privacyEpoch !== epoch || !allowed() || document.hidden) return;
      const chapter = selectedChapter(guide);
      if (!chapters.has(chapter) || (expectedChapter && chapter !== expectedChapter) ||
          (before !== undefined && before === chapter)) return;
      const key = [guide.dataset.guideProduct, chapter, entryPoint].join(':');
      const now = performance.now();
      const previous = lastActions.get(key);
      if (previous !== undefined && now - previous < 2000) return;
      if (send('brew_guide_select', guide, chapter, entryPoint)) lastActions.set(key, now);
      for (const [oldKey, at] of lastActions) if (now - at >= 2000) lastActions.delete(oldKey);
    });
  }

  document.addEventListener('click', event => {
    if (!event.isTrusted || !(event.target instanceof Element) || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const tab = event.target.closest('.coffee-chapter-guide__tabs button');
    const guide = tab?.closest(selector);
    if (guide && !tab.disabled) {
      recordSelection(guide, 'chapter_tab', tab.id.split('-').at(-1));
      return;
    }
    const shortcut = event.target.closest('a.kaizen-roast-rest-link');
    const targetGuide = shortcut?.hash && document.getElementById(shortcut.hash.slice(1))?.closest(selector);
    if (targetGuide) recordSelection(targetGuide, 'roast_date_shortcut', shortcut.hash.split('-').at(-1));
  }, {capture: true});

  document.addEventListener('keydown', event => {
    if (!event.isTrusted || !(event.target instanceof Element) ||
        !['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
    const guide = event.target.closest('.coffee-chapter-guide__tabs button')?.closest(selector);
    if (guide) recordSelection(guide, 'chapter_tab', null, selectedChapter(guide));
  }, {capture: true});

  document.addEventListener('visitorConsentCollected', () => {
    privacyEpoch++;
    for (const [guide, state] of states) considerView(guide, state);
  });
  document.addEventListener('visibilitychange', () => {
    for (const [guide, state] of states) considerView(guide, state);
  });
  new MutationObserver(records => {
    for (const record of records) for (const node of record.addedNodes) if (node instanceof Element) register(node);
    for (const [guide, state] of states) if (!guide.isConnected) {
      cancelView(state);
      observer?.unobserve(state.controls);
      states.delete(guide);
    }
  }).observe(document.documentElement, {childList: true, subtree: true});
  register(document);
})();
